// Shared by both forks.
import React, { useCallback } from 'react';
import { resetStickySearchInAssetbank } from '../helpers/searchUtils';
import type { SearchOptionType } from '../helpers/types';
import type { AfterSelectAction } from '../KeyboardNavigation/types';
import { useSpotlightCapabilities } from './useSpotlightCapabilities';
import type { SearchAction } from '../SpotlightState';

type Options = {
	refInput: React.RefObject<HTMLInputElement | null>;
	cancelPendingSearch: () => void;
	dispatch: React.Dispatch<SearchAction>;
	openSpotlightDropdown: () => void;
	closeSpotlightDropdown: () => void;
	isApplyingSimilarityFilter: boolean;
	isHybridSearchOn: boolean;
	blurOnClose?: boolean;
	reopenOnRemoveOption?: boolean;
};

export function useSpotlightClose({
	refInput,
	cancelPendingSearch,
	dispatch,
	openSpotlightDropdown,
	closeSpotlightDropdown,
	isApplyingSimilarityFilter,
	isHybridSearchOn,
	blurOnClose = false,
	reopenOnRemoveOption = false,
}: Options) {
	const { canShowStickySearch } = useSpotlightCapabilities();

	const clearResults = () => {
		cancelPendingSearch();
		dispatch({ type: 'resultsCleared' });

		if (canShowStickySearch) {
			resetStickySearchInAssetbank(isHybridSearchOn, isApplyingSimilarityFilter);
		}

		if (refInput.current) {
			refInput.current.focus();
		}
	};

	const onClearButtonClick = () => {
		clearResults();
		closeSpotlightDropdown();
		dispatch({ type: 'searchOptionsChanged', options: [] });
	};

	const removeSelectedSearchOption = (chipKey: SearchOptionType) => {
		dispatch({ type: 'searchOptionRemoved', key: chipKey });

		if (reopenOnRemoveOption) {
			openSpotlightDropdown(); // If user closes the dropdown, we re-open to show updated results for the search query after removing a search option
		}
	};

	const closeSpotlight = useCallback(() => {
		dispatch({ type: 'searchSettled' });
		closeSpotlightDropdown();

		if (blurOnClose) {
			refInput.current?.blur();
		}
	}, [blurOnClose, closeSpotlightDropdown, dispatch, refInput]);

	const closeAndClearSpotlight = useCallback(() => {
		closeSpotlight();

		if (!canShowStickySearch) {
			dispatch({ type: 'searchOptionsChanged', options: [] });
			dispatch({ type: 'termChanged', term: '' });
		}
	}, [canShowStickySearch, closeSpotlight, dispatch]);

	const onAfterSelect = useCallback(
		(afterSelect: Exclude<AfterSelectAction, 'none'>) =>
			afterSelect === 'close' ? closeSpotlight() : closeAndClearSpotlight(),
		[closeSpotlight, closeAndClearSpotlight],
	);

	return {
		clearResults,
		onClearButtonClick,
		removeSelectedSearchOption,
		closeSpotlight,
		closeAndClearSpotlight,
		onAfterSelect,
	};
}
